import { firstValueFrom, from } from 'rxjs';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  DELETE_SUCCESS,
  SAVE_SUCCESS,
  SYS_ERROR,
  UPDATE_SUCCESS,
} from '../../../shared/common/message/MessageCommon';
import { User } from '../../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class UserDataService {
  currentUser: any;
  userPath: string = 'users';
  constructor(
    private db: AngularFirestore,
    private fns: AngularFireFunctions,
    private message: NzMessageService
  ) {
    this.currentUser = localStorage.getItem('currentUser')?JSON.parse(localStorage.getItem('currentUser') || ''):null;
  }

  async getAllUser() {
    let collection = this.db.collection<User>(this.userPath).get();

    const newsDataDB = await firstValueFrom(collection);
    let newsData: any[] = newsDataDB.docs.map((doc) => doc.data()) || [];
    return [...newsData];
  }

  getUserById(id: string) {
    return this.db.collection<User>(this.userPath, (ref) =>
      ref.where('id', '==', id)
    );
  }

  /**
   * Danh sách NHÂN VIÊN để chọn trên hóa đơn — nay lấy thẳng từ `users`.
   *
   * Trước đây có collection `employee` riêng chỉ chứa mỗi tên, tách rời hoàn
   * toàn với tài khoản đăng nhập: cùng một con người phải nhập ở hai nơi, và
   * doanh số không quy được về người đăng nhập. Nay gom về một mối.
   *
   * Trả về `{ id, name }` với `id` = Document ID = Auth uid, để
   * `order.employee_id` trỏ thẳng tới tài khoản đã lên đơn.
   *
   * ⚠️ Đừng nhầm với `users.employee_id` — trường đó là MÃ nhân viên dạng
   * `FOX_ST001`, không phải khóa tham chiếu.
   */
  async getStaffOptions(): Promise<{ id: string; name: string }[]> {
    const snap = await firstValueFrom(
      this.db.collection<User>(this.userPath).get()
    );
    return snap.docs
      .map((d) => ({ ...(d.data() as any), id: d.id }))
      .filter((u) => u.deleted !== true)
      .map((u) => ({
        id: u.id,
        name: (u.employee_name || u.user_name || '').toString(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
  }

  /**
   * Đọc hồ sơ theo Document ID (= Firebase Auth uid).
   *
   * Dùng sau khi đã đăng nhập, nên không cần quyền đọc cả collection như
   * `getUserByEmail`. Trả về null nếu không có hồ sơ.
   */
  async getUserDocById(uid: string): Promise<any | null> {
    const doc = await firstValueFrom(
      this.db.collection<User>(this.userPath).doc(uid).get()
    );
    if (!doc.exists) return null;
    return { ...(doc.data() as any), id: doc.id };
  }

  async getUserByEmail(email: string) {
    let collection = this.db.collection<User>(this.userPath, (ref) =>
    ref.where('email', '==', email)).get();

    const newsDataDB = await firstValueFrom(collection);
    let newsData: any[] = newsDataDB.docs.map((doc) => doc.data()) || [];
    const first = newsData[0];
    return {
      id: first?.id,
      role: first?.role,
      login_error_count: first?.login_error_count ? first?.login_error_count : 0,
    };
  }
  
  add(data: any): void {
    this.db
      .collection<User>(this.userPath)
      .doc(data.id)
      .set({ ...data })
      .then(() => {
        this.message.success(SAVE_SUCCESS);
      })
      .catch((err) => {
        this.message.error(SYS_ERROR);
      });
  }

  /**
   * Tạo tài khoản đăng nhập THẬT cho nhân viên/quản lý.
   *
   * Gọi Cloud Function `createUser` (functions/src/index.ts) thay vì tự tạo ở
   * client, vì 3 lý do:
   *  1. Function kiểm "người gọi có phải Admin không" bằng Admin SDK — kiểm ở
   *     client thì ai mở DevTools cũng qua được.
   *  2. `createUserWithEmailAndPassword` ở client tự đăng nhập luôn user vừa
   *     tạo, nên bản cũ phải dựng app phụ "UserCreator" để giữ phiên Admin.
   *  3. Lỗi giữa chừng được dọn ngay trên server, không để lại Auth account mồ
   *     côi làm SĐT đó không tạo lại được.
   *
   * KHÔNG nhận tham số mật khẩu: server tự lấy 6 số cuối CCCD theo đúng quy
   * ước đang dùng, rồi chỉ đẩy lên Auth — không bao giờ ghi xuống Firestore.
   * Trả về uid; ném lỗi (đã hiển thị message) nếu thất bại.
   */
  async createAccount(
    user: any
  ): Promise<{ uid: string; login_email: string }> {
    try {
      const call = this.fns.httpsCallable<
        any,
        { uid: string; login_email: string }
      >('createUser');
      const res = await firstValueFrom(
        call({
          // Màn tạo tài khoản đã gỡ ô "Tên đăng nhập", nên tên hiển thị lấy từ
          // `employee_name`.
          //
          // ⚠️ VẪN PHẢI GỬI, đừng bỏ trống. Function đang chạy trên Firebase có
          // thể là bản cũ — bản đó bắt buộc `user_name` dài ≥4 ký tự và từ chối
          // thẳng: "Tên đăng nhập tối thiểu 4 ký tự." Gửi sẵn như thế này thì
          // chạy được với CẢ hai bản, không phụ thuộc vào việc function đã
          // deploy hay chưa (bản mới cũng tôn trọng giá trị client gửi lên).
          user_name: user.employee_name,
          phone: user.phone,
          cccd: user.cccd,
          employee_id: user.employee_id,
          employee_name: user.employee_name,
          role: user.role,
          // Email liên hệ — KHÔNG phải định danh đăng nhập. Server bỏ qua nếu
          // là bản cũ, nên có bước ghi bù ngay bên dưới.
          contact_email: user.contact_email || '',
          // Bỏ trống thì server lấy 6 số cuối CCCD.
          password: user.password || '',
        })
      );
      const uid = res?.uid || '';
      // ⚠️ GHI BÙ `contact_email`.
      //
      // Bản `createUser` đang chạy trên Firebase chưa biết tới field này (nó
      // ra đời trước), nên hồ sơ vừa tạo sẽ không có email liên hệ và màn danh
      // sách hiện ô trống. Admin có quyền ghi `users` nên ghi thêm được từ
      // client. Khi function mới được deploy thì nó tự ghi sẵn, lệnh này chỉ
      // ghi lại đúng giá trị cũ — vô hại.
      //
      // Hỏng ở bước này KHÔNG được làm hỏng cả việc tạo tài khoản: tài khoản
      // đã tạo xong rồi, thiếu mỗi email liên hệ thì sửa tay sau được.
      if (uid && user.contact_email) {
        try {
          await this.db
            .collection(this.userPath)
            .doc(uid)
            .update({ contact_email: user.contact_email });
        } catch (e) {
          console.warn('Không ghi được contact_email:', e);
        }
      }
      this.message.success(SAVE_SUCCESS);
      return {
        uid,
        login_email: res?.login_email || '',
      };
    } catch (err: any) {
      // HttpsError từ function: `code` dạng 'already-exists', `message` là câu
      // tiếng Việt đã soạn sẵn ở server nên hiển thị thẳng được.
      const code = err?.code || '';
      const text = err?.message || '';
      if (code.indexOf('unauthenticated') !== -1) {
        this.message.error('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.');
      } else if (text) {
        this.message.error(text);
      } else {
        console.error('createAccount error:', err);
        this.message.error(SYS_ERROR);
      }
      throw err;
    }
  }

  edit(data: any): void {
    this.db
      .collection<User>(this.userPath)
      .doc(data.id)
      .set({ ...data })
      .then(() => {
        this.message.success(UPDATE_SUCCESS);
      })
      .catch((err) => {
        this.message.error(SYS_ERROR);
      });
  }

  delete(data: any) {
    return from (
    this.db
      .collection<User>(this.userPath)
      .doc(data.id)
      .set({ ...data })
      .then(() => {})
      .catch((err) => {
        this.message.error(SYS_ERROR);
      })
    )
  }

  // ⚠️ ĐÃ BỎ `regeneratedPassword()`.
  //
  // Hàm cũ ghi mật khẩu dạng CHỮ RÕ vào `users/{id}.password`, trong khi rules
  // cho mọi tài khoản đã đăng nhập đọc collection này (màn đăng nhập phải tra
  // `users` theo email trước khi có phiên Auth nên không siết read được). Kết
  // quả: nhân viên mở DevTools là đọc được mật khẩu của Admin.
  //
  // Tệ hơn, nó KHÔNG đổi mật khẩu thật trên Firebase Auth — mật khẩu hiển thị
  // ra màn hình không đăng nhập được.
  //
  // Thay thế:
  //  - Tự đổi mật khẩu của mình: `UpdatePasswordComponent` gọi thẳng
  //    `credential.user.updatePassword()` (đổi thật, không ghi Firestore).
  //  - Admin cấp lại mật khẩu cho người khác: client không làm được, phải có
  //    Cloud Functions + Admin SDK (`auth().updateUser()`). Xem
  //    `UserListComponent.resetPassword()`.

  generatePassword(length = 16) {
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let retVal = '';
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }

  updateLoginErrorCount(userId : string, countError: number) {
    return from(
      this.db
      .collection<User>('users')
      .doc(userId).update({login_error_count : countError})
    )
  }
}
